/** "จุดวัดน้ำท่วมสูงสุด" (SPEC v0.7 §3 + v0.9.1 §4). Two SEPARATE sources, never
 *  merged into one ranking because their units differ:
 *    • POPNIX — ซม. น้ำบนถนน, กทม. only.
 *    • ThaiWater (สสน.) — ม. เหนือตลิ่ง, ทั่วประเทศ.
 *  Both rank only REAL over-bank/over-road readings (no MSL level, m³/s, or DEM). */
import { loadGaugeData, floodGauges, depthIcon, depthLabel, POPNIX_FRESH_HOURS, type FloodGauge } from "./bkk-layer";
import { rankThaiWater, ensureThaiWaterData, THAIWATER_CREDIT, type TWStation } from "./thaiwater-layer";
import { emergencyCard } from "./emergency";
import { depthAdviceHtml } from "./depth-ui";

function el<K extends keyof HTMLElementTagNameMap>(tag: K, attrs: Record<string, string> = {}, ...kids: (Node | string)[]): HTMLElementTagNameMap[K] {
  const n = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs)) { if (k === "html") n.innerHTML = v; else n.setAttribute(k, v); }
  n.append(...kids);
  return n;
}

export interface TopFloodOpts {
  center: { lat: number; lon: number } | null;
  radiusKm: number;
  onPick: (g: FloodGauge) => void;
  onPickStation?: (lat: number, lon: number, label: string) => void;
  onClose: () => void;
}

export async function renderTopFlood(host: HTMLElement, opts: TopFloodOpts): Promise<void> {
  host.replaceChildren(el("div", { class: "result card-in" }, el("div", { class: "tf-head" }, el("b", {}, "🔝 จุดวัดน้ำท่วมสูงสุด"), el("button", { class: "btn ghost sm", id: "tf-close" }, "✕ ปิด")), el("div", { class: "muted small" }, "กำลังโหลดจุดวัดจริง…")));
  host.querySelector("#tf-close")?.addEventListener("click", opts.onClose);
  await Promise.all([loadGaugeData(), ensureThaiWaterData()]);

  let source: "popnix" | "thaiwater" = "popnix";
  let scope: "radius" | "all" = opts.center ? "radius" : "all";

  const draw = (): void => {
    const useCenter = scope === "radius" ? opts.center : null;
    const radius = scope === "radius" ? opts.radiusKm : null;

    const sourceRow = el("div", { class: "tf-scope" },
      tab("น้ำท่วมถนน กทม. (ซม., POPNIX)", source === "popnix", () => { source = "popnix"; draw(); }, false),
      tab("แม่น้ำล้นตลิ่งสูงสุด (ม., ThaiWater)", source === "thaiwater", () => { source = "thaiwater"; draw(); }, false),
    );
    const scopeRow = el("div", { class: "tf-scope" },
      tab("ในรัศมี " + opts.radiusKm + " กม.", scope === "radius", () => { if (!opts.center) return; scope = "radius"; draw(); }, !opts.center),
      tab("ทุกจุดที่มีข้อมูล", scope === "all", () => { scope = "all"; draw(); }, false),
    );

    const rows: Node[] = [];
    let staleBlock: Node = document.createTextNode("");
    let subtitle: string;

    if (source === "popnix") {
      const { fresh, stale } = floodGauges(useCenter, radius ?? undefined);
      subtitle = "ความลึกน้ำล้นตลิ่ง/บนถนนจากจุดวัดจริง (POPNIX) · ครอบคลุมเฉพาะ กทม.";
      if (fresh.length === 0) rows.push(emptyRow(scope, opts.radiusKm, "ความลึกมากกว่า 0"));
      else fresh.forEach((g, i) => rows.push(popnixRow(i + 1, g, opts.onPick)));
      if (stale.length) staleBlock = el("details", { class: "tf-stale" }, el("summary", {}, `ข้อมูลเก่ากว่า ${POPNIX_FRESH_HOURS} ชม. (${stale.length}) — ไม่จัดอันดับ`), ...stale.map((g) => popnixRow(0, g, opts.onPick, true)));
    } else {
      const { fresh, staleCount } = rankThaiWater(useCenter, radius);
      subtitle = "ระดับน้ำเหนือตลิ่งจากสถานีจริง (ThaiWater/สสน.) · ทั่วประเทศ";
      if (fresh.length === 0) rows.push(emptyRow(scope, opts.radiusKm, "น้ำล้นตลิ่ง"));
      else fresh.forEach((s, i) => rows.push(twRow(i + 1, s, opts.onPickStation)));
      if (staleCount) staleBlock = el("div", { class: "muted small", style: "padding:6px 2px" }, `+ สถานีล้นตลิ่งที่ข้อมูลเก่ากว่า 3 ชม. อีก ${staleCount} แห่ง (ไม่จัดอันดับ)`);
    }

    host.replaceChildren(el("div", { class: "result card-in" },
      el("div", { class: "tf-head" }, el("b", {}, "🔝 จุดวัดน้ำท่วมสูงสุด"), el("button", { class: "btn ghost sm", id: "tf-close" }, "✕ ปิด")),
      sourceRow,
      el("div", { class: "muted small" }, subtitle),
      scopeRow,
      mapKey(source),
      el("div", { class: "tf-list" }, ...rows),
      staleBlock,
      el("div", { class: "muted small", style: "margin-top:6px" }, source === "thaiwater" ? THAIWATER_CREDIT : "ข้อมูล: สำนักการระบายน้ำ กทม. (POPNIX)"),
      emergencyCard(),
    ));
    host.querySelector("#tf-close")?.addEventListener("click", opts.onClose);
  };
  draw();
}

/** Explain existing map colours without mixing cm readings and river/dam status. */
function mapKey(source: "popnix" | "thaiwater"): HTMLElement {
  const details = el("details", {},
    el("summary", {}, "ความหมายสีจุดวัดและวงกระเพื่อม"),
    el("div", {}, "วงกระเพื่อมรอบเขื่อนแสดงสถานะน้ำ/การระบายของเขื่อน ไม่ใช่ความลึกน้ำท่วม ณ จุดวัด"),
    el("div", {}, "วงส้ม = น้ำตั้งแต่ 80% แต่ต่ำกว่า 100% ของความจุ"),
    el("div", {}, "วงแดง = น้ำตั้งแต่ 100% ของความจุ หรือเขื่อนเจ้าพระยาระบายตั้งแต่ 1,000 ลบ.ม./วินาที"),
    el("div", {}, "เขื่อนที่มีน้ำน้อยกว่า 80% อาจมีวงสีตามปริมาณน้ำเมื่อระบายตั้งแต่ 50 ลบ.ม./วินาที"),
  );
  const key = el("div", { class: "tf-map-key" },
    el("div", { class: "tf-key-row" }, el("i", { class: "tf-key-dot tf-key-ring", "aria-hidden": "true" }), "แตะรายการ → หมุดสีฟ้าชี้จุดวัดที่เลือก"),
    details,
  );
  if (source === "popnix") {
    details.append(el("div", {}, "สีจุดวัด กทม. ตามความลึก (ซม.)"),
      el("div", { class: "tf-depth-key" }, ...[
        ["#9fd3ff", "น้อยกว่า 20"], ["#2f7bff", "20–<50"], ["#7b3fe4", "50–<100"],
        ["#c026d3", "100–<150"], ["#ff3b5c", "ตั้งแต่ 150"], ["#8a96a6", "ข้อมูลเก่า"],
      ].map(([color, label]) => el("span", {}, el("i", { class: "tf-key-dot", style: `background:${color}`, "aria-hidden": "true" }), label!))));
  }
  return key;
}

function emptyRow(scope: string, radiusKm: number, what: string): HTMLElement {
  return el("div", { class: "muted small", style: "padding:8px 2px" },
    `ไม่พบ${what}ในจุดวัดที่มีข้อมูลล่าสุด` + (scope === "radius" ? ` (ในรัศมี ${radiusKm} กม.)` : ""));
}
function tab(label: string, active: boolean, on: () => void, disabled: boolean): HTMLElement {
  const b = el("button", { class: "tf-tab" + (active ? " active" : "") + (disabled ? " disabled" : "") }, label);
  if (!disabled) b.addEventListener("click", on);
  return b;
}
function popnixRow(rank: number, g: FloodGauge, onPick: (g: FloodGauge) => void, stale = false): HTMLElement {
  const r = el("button", { class: "tf-row" + (stale ? " stale" : "") },
    el("span", { class: "tf-rank" }, stale ? "–" : String(rank)),
    el("span", { class: "tf-ico" }, depthIcon(g.overtopCm)),
    el("span", { class: "tf-name" }, el("b", {}, g.name), el("span", { class: "muted small" }, `${g.distKm != null ? "ห่าง " + g.distKm.toFixed(1) + " กม. · " : ""}${g.measuredAt ?? "—"} · POPNIX`)),
    el("span", { class: "tf-cm" }, `${Math.round(g.overtopCm)}`, el("span", { class: "tf-unit" }, " ซม."), el("span", { class: "muted small tf-bl" }, depthLabel(g.overtopCm))),
    el("span", { class: "tf-advice", html: depthAdviceHtml(g.overtopCm) }), // D8: same measured-depth advice
  );
  r.addEventListener("click", () => onPick(g));
  return r;
}
function twRow(rank: number, s: TWStation, onPick?: (lat: number, lon: number, label: string) => void): HTMLElement {
  const m = s.diffBankM ?? 0;
  const r = el("button", { class: "tf-row" },
    el("span", { class: "tf-rank" }, String(rank)),
    el("span", { class: "tf-ico" }, "📏"),
    el("span", { class: "tf-name" }, el("b", {}, s.name), el("span", { class: "muted small" }, `${s.river ? s.river + " · " : ""}${s.distKm != null ? "ห่าง " + s.distKm.toFixed(1) + " กม. · " : ""}${s.datetime ? s.datetime.slice(11) : "—"}`)),
    el("span", { class: "tf-cm" }, `+${m.toFixed(2)}`, el("span", { class: "tf-unit" }, " ม."), el("span", { class: "muted small tf-bl" }, "เหนือตลิ่ง")),
  );
  if (onPick) r.addEventListener("click", () => onPick(s.lat, s.lon, `📍 สถานี ${s.name}`));
  return r;
}
